import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    UpdateLeadCustomFieldValue as UpdateLeadCustomFieldValueMutation,
    UpdateLeadCustomFieldValueVariables as UpdateLeadCustomFieldValueVariables,
    UpdateLeadInputFieldInput,
    Lead as LeadFragment,
} from 'nutshell-graphql-types';

import LEAD_FRAGMENT from '../fragments/lead-fragment.graphql';
import UPDATE_LEAD_CUSTOM_FIELD_VALUE from '../mutations/update-lead-custom-field-value.graphql';

export const useUpdateLead = (): {
    updateLead: (
        input: UpdateLeadInputFieldInput,
        leadData?: LeadFragment | null | undefined
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [updateLeadCustomFieldValue, {loading, error}] = useMutation<
        UpdateLeadCustomFieldValueMutation,
        UpdateLeadCustomFieldValueVariables
    >(UPDATE_LEAD_CUSTOM_FIELD_VALUE);

    const updateLead = React.useCallback(
        (input: UpdateLeadInputFieldInput, leadData?: LeadFragment | null) => {
            // If no leadData is provided, use simple mutation without optimistic response
            if (!leadData) {
                return updateLeadCustomFieldValue({variables: {lead: input}});
            }

            // Logic migrated from lead-header-left.js
            // We need to modify the leadName a little bit, because the name is
            // generated server side from the description.
            let leadName = '';
            let newLeadDueDate;

            if (input.description === undefined) {
                // We aren't changing the lead description on this update. Use the existing name
                leadName = leadData.name;
            } else if (input.description === null) {
                // Until we can get the response from the server, let the user know it's going to change
                leadName = 'determining lead name…';
            } else {
                // We are setting the lead description, so use the new value
                leadName = input.description;
            }

            if (input.dueTime === undefined) {
                newLeadDueDate = leadData.anticipatedClosedTime;
            } else if (input.dueTime === null) {
                newLeadDueDate = null;
            } else {
                newLeadDueDate = input.dueTime;
            }

            let optimisticTags = [];
            if (input.tags) {
                optimisticTags = input.tags.map((tag) => ({
                    __typename: 'Tag',
                    name: tag.name,
                    count: 0,
                    // @ts-expect-error
                    id: tag.id ? tag.id : 'NEW_TAG_ID',
                }));
            } else {
                optimisticTags = leadData.tags;
            }

            // Prepare the input for mutation
            const mutationInput = {...input} as const;
            if (mutationInput.tags) {
                // @ts-expect-error
                mutationInput.tags = mutationInput.tags.map((tag) => {
                    return {
                        name: tag.name,
                        tagType: 'LEAD',
                    };
                });
            }

            return updateLeadCustomFieldValue({
                variables: {lead: mutationInput},
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateLead: {
                        __typename: 'UpdateLeadPayload',
                        lead: {
                            // @ts-expect-error
                            __typename: 'Lead',
                            ...leadData,
                            ...input,
                            name: leadName,
                            anticipatedClosedTime: newLeadDueDate,
                            // @ts-expect-error
                            tags: optimisticTags,
                        },
                    },
                },
                update: (proxy, mutationResults) => {
                    const updatedLead =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.updateLead &&
                        mutationResults.data.updateLead.lead;

                    if (!updatedLead) return;

                    proxy.writeFragment({
                        id: input.id,
                        fragment: LEAD_FRAGMENT,
                        fragmentName: 'Lead',
                        data: {
                            ...updatedLead,
                        },
                    });
                },
            });
        },
        [updateLeadCustomFieldValue]
    );

    return {
        updateLead,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
